import { FeedbackForm, FeedbackFormElement, FeedbackFormElementSelect, FeedbackFormElementText, FeedbackFormElementTypeEnum } from '../types';
import { logger } from '../utils/logger';

export const validateIsEmpty = (value: string | number | null = ''): boolean => {
  return !`${value}`.trim();
};

export const validateGreaterOrEqualThen = (
  value: string,
  length = 8
): boolean => {
  return value.length >= length;
};

export const validateLessOrEqualThen = (
  value: string,
  length = 255
): boolean => {
  return value.length <= length;
};

export const isNumber = (value: string): boolean => {
  return /^\d*$/.test(value)
}
// export const validateRegexp = (value: string, regex: RegExp): boolean => {
//   return regex.test(value);
// };

// const specialCharacter = /(?=.*[!@#$%^&*])/;
// const numberCharacter = /(?=.*[0-9])/;
const emailRegexp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// const strongPasswordRegexp = /^(?=.{8,}$)(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*?\W).*$/;

export const GENDER_MAX_ANSWER_LIMIT = 20;

export const validatePassword = (
  value: string,
  { minLength = 8, maxLength = 255 } = {}
): string | undefined => {
  if (validateIsEmpty(value)) {
    return 'Please fill in password.';
  }

  if (!validateGreaterOrEqualThen(value, minLength)) {
    return `Password should be at least ${minLength} characters.`;
  }

  if (!validateLessOrEqualThen(value)) {
    return `Password should be no more then ${maxLength} characters.`;
  }

  return undefined;
};

export const validateEmail = (value: string | number): string | undefined => {
  if (!emailRegexp.test(value.toString())) {
    return 'Please provide a valid email address.';
  }
  return undefined;
};

export const isNotEmail = (value: string | number | undefined): boolean => {
  if (!value) return true;
  return !emailRegexp.test(value.toString());
};

export const isEmptyObject = (value?: Object) => !!value && Object.keys(value).length === 0;
export const hasError = (value: Object) => Object.values(value).some(ele => !!ele)

export const isEmptyObjectWithoutUndefined = (value: Object) => !value || Object.keys(value).length === 0 || Object.keys(value).filter(key => value[key] !== undefined).length === 0;

export const feedbackFormHasErrors = (form: FeedbackForm) => {
  const elements = form.elements;

  if (!form.surveyType) return true;

  for(let ii = 0; ii < elements.length; ii++) {
    const el = elements[ii];

    if (validateIsEmpty(el.value)) {
      return true;
    }

    const options = (el as FeedbackFormElementSelect)?.options;
    const assessments = (el as FeedbackFormElementSelect)?.assessments;

    if (el.type === FeedbackFormElementTypeEnum.checkbox || el.type === FeedbackFormElementTypeEnum.radio) {
      if ((options?.length ?? 0) <= 1) {
        return true;
      }
    }
    if (el.type === FeedbackFormElementTypeEnum.checkboxAssessable || el.type === FeedbackFormElementTypeEnum.radioAssessable) {
      if (!assessments || (assessments?.length ?? 0) <= 1) {
        return true;
      } else {
        if(assessments.every(el => el === false)) {
          return true;
        }
      }
    }
    if (options?.length) {
      for (let ii = 0; ii < options.length; ii++) {
        const o = options[ii];

        if (validateIsEmpty(o)) {
          return true;
        }
      }
    }
  }

  return false;
}

export const feedbackFormHasAnswerErrors = (elements: FeedbackFormElement[]) => {
  for(let ii = 0; ii < elements.length; ii++) {
    const el = elements[ii];

    switch (el.type) {
      case FeedbackFormElementTypeEnum.radio:
      case FeedbackFormElementTypeEnum.radioAssessable:
      case FeedbackFormElementTypeEnum.text: {
        if (!el.answer?.value) {
          logger.debug(`[Validation] Question ${ii+1} (${el.type}) is missing an answer`);
          return true;
        }
        const e = el as FeedbackFormElementText;
        if (e.lengthLimit && e.type === FeedbackFormElementTypeEnum.text && e.answer?.value?.length! > GENDER_MAX_ANSWER_LIMIT) {
          logger.debug(`[Validation] Question ${ii+1} (${el.type}) exceeds length limit`);
          return true;
        }
        break;
      }
      case FeedbackFormElementTypeEnum.textarea: {
        if (!el.answer?.value) {
          logger.debug(`[Validation] Question ${ii+1} (${el.type}) is missing an answer`);
          return true;
        }
        break;
      }
      case FeedbackFormElementTypeEnum.checkbox:
      case FeedbackFormElementTypeEnum.checkboxAssessable: {
        if (!el?.answer?.value?.length) {
          logger.debug(`[Validation] Question ${ii+1} (${el.type}) is missing an answer`);
          return true;
        }
        break;
      }
      case FeedbackFormElementTypeEnum.multiScale:
      case FeedbackFormElementTypeEnum.multiScaleAssessable: {
        if (!el?.answer || Object.keys(el?.answer || {}).length < (el?.statements?.length || 0)) {
          logger.debug(`[Validation] Question ${ii+1} (${el.type}) is missing answers for some statements`);
          return true;
        }
        break;
      }
      default: {
        // Handle any unknown question types
        logger.debug(`[Validation] Unknown question type: ${(el as any).type}`);
      }
    }

    if (el.askExplanation && (!el.answer?.answerExplanation || el.answer?.answerExplanation === '')) {
      logger.debug(`[Validation] Question ${ii+1} (${el.type}) is missing explanation`);
      return true;
    }
  }

  return false;
}

export const feedbackSingleFormHasAnswerError = (element: FeedbackFormElement) => {
  switch (element.type) {
    case FeedbackFormElementTypeEnum.radio:
    case FeedbackFormElementTypeEnum.radioAssessable:
    case FeedbackFormElementTypeEnum.text: {
      if (!element.answer?.value) {
        logger.debug(`[Validation] Single question (${element.type}) is missing an answer`);
        return true;
      }
      const e = element as FeedbackFormElementText;
      if (e.lengthLimit && e.type === FeedbackFormElementTypeEnum.text && e.answer?.value?.length! > GENDER_MAX_ANSWER_LIMIT) {
        logger.debug(`[Validation] Single question (${element.type}) exceeds length limit`);
        return true;
      }
      break;
    }
    case FeedbackFormElementTypeEnum.textarea: {
      if (!element.answer?.value) {
        logger.debug(`[Validation] Single question (${element.type}) is missing an answer`);
        return true;
      }
      break;
    }
    case FeedbackFormElementTypeEnum.checkbox:
    case FeedbackFormElementTypeEnum.checkboxAssessable: {
      if (!element?.answer?.value?.length) {
        logger.debug(`[Validation] Single question (${element.type}) is missing an answer`);
        return true;
      }
      break;
    }
    case FeedbackFormElementTypeEnum.multiScale:
    case FeedbackFormElementTypeEnum.multiScaleAssessable: {
      if (!element?.answer || Object.keys(element?.answer || {}).length < (element?.statements?.length || 0)) {
        logger.debug(`[Validation] Single question (${element.type}) is missing answers for some statements`);
        return true;
      }
      break;
    }
    default: {
      // Handle any unknown question types
      logger.debug(`[Validation] Unknown question type: ${(element as any).type}`);
    }
  }

  if ((element as FeedbackFormElement).askExplanation && (!(element as FeedbackFormElement).answer?.answerExplanation || (element as FeedbackFormElement).answer?.answerExplanation === '')) {
    logger.debug(`[Validation] Single question (${(element as any).type}) is missing explanation`);
    return true;
  }

  return false;
}

export function generateRandomString(length: number): string {
  const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
  const digitChars = '0123456789';
  const specialChars = '!@#$%^&*()';

  const allChars = uppercaseChars + lowercaseChars + digitChars + specialChars;
  let randomString = '';

  // Include at least one uppercase character
  randomString += uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length));

  // Include at least one digit
  randomString += digitChars.charAt(Math.floor(Math.random() * digitChars.length));

  // Include a special character
  randomString += specialChars.charAt(Math.floor(Math.random() * specialChars.length));

  // Fill the remaining length with random characters
  for (let i = 0; i < length - 3; i++) {
    randomString += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }

  // Shuffle the characters in the string
  randomString = randomString.split('').sort(() => Math.random() - 0.5).join('');

  return randomString;
}

export const hasAllValues = (obj: Object) => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (!value && value !== 0) {
        return false;
      }
    }
  }
  return true;
}

export const isJSON = (value: any) => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
}
